import React, { useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import axios from '../../../utils/axiosWithAuth';

const DailyEmailStats = () => {
  // ⏰ Set default month to current in IST
  const currentISTMonth = DateTime.now().setZone('Asia/Kolkata').toFormat('yyyy-MM');

  const [dailyStats, setDailyStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(currentISTMonth);
  const [specificDate, setSpecificDate] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStats();
  }, [page, month, specificDate]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError('');

      const params = { page };

      // Convert filters to IST and format
      if (specificDate) {
        const istDate = DateTime.fromISO(specificDate).setZone('Asia/Kolkata');
        params.date = istDate.toFormat('yyyy-MM-dd');
      } else if (month) {
        const istMonth = DateTime.fromFormat(month, 'yyyy-MM').setZone('Asia/Kolkata');
        params.month = istMonth.toFormat('yyyy-MM');
      }

      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/email/stats-daily`, {
        params,
      });

      setDailyStats(res.data.stats || []);
      setTotalPages(res.data.pagination?.totalPages || 1);
    } catch (err) {
      console.error('Failed to load daily stats:', err);
      setError('❌ Failed to fetch email stats.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (isoDateStr) => {
    return DateTime.fromISO(isoDateStr, { zone: 'Asia/Kolkata' }).toFormat('dd LLL yyyy');
  };

  const handleMonthChange = (e) => {
    setMonth(e.target.value);
    setSpecificDate('');
    setPage(1);
  };

  const handleDateChange = (e) => {
    setSpecificDate(e.target.value);
    setMonth('');
    setPage(1);
  };

  const clearFilters = () => {
    setMonth(currentISTMonth);
    setSpecificDate('');
    setPage(1);
  };

  const nextPage = () => page < totalPages && setPage(page + 1);
  const prevPage = () => page > 1 && setPage(page - 1);

  return (
    <div className="container py-4">
      <h3>📊 Daily Email Stats</h3>

      <div className="d-flex align-items-center my-3 flex-wrap gap-3">
        <div>
          <label htmlFor="month" className="form-label mb-0">📅 Filter by Month:</label>
          <input
            type="month"
            id="month"
            className="form-control"
            value={month}
            onChange={handleMonthChange}
            style={{ maxWidth: 200 }}
          />
        </div>

        <div>
          <label htmlFor="date" className="form-label mb-0">📆 Filter by Date:</label>
          <input
            type="date"
            id="date"
            className="form-control"
            value={specificDate}
            onChange={handleDateChange}
            style={{ maxWidth: 200 }}
          />
        </div>

        {(month !== currentISTMonth || specificDate) && (
          <button className="btn btn-secondary align-self-end" onClick={clearFilters}>
            ❌ Clear
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-5">⏳ Loading daily email stats...</div>
      ) : error ? (
        <div className="alert alert-danger text-center">{error}</div>
      ) : (
        <>
          <table className="table table-bordered mt-3">
            <thead className="table-light">
              <tr>
                <th>Date</th>
                <th>Auto</th>
                <th>Manual</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {dailyStats.length > 0 ? (
                dailyStats.map((stat) => (
                  <tr key={stat.date}>
                    <td>{formatDate(stat.date)}</td>
                    <td>{stat.auto?.count || 0}</td>
                    <td>{stat.manual?.count || 0}</td>
                    <td>{stat.total}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center">No stats found.</td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="d-flex justify-content-between align-items-center mt-3">
            <button className="btn btn-outline-primary" onClick={prevPage} disabled={page === 1}>
              ⬅️ Previous
            </button>
            <span>Page {page} of {totalPages}</span>
            <button className="btn btn-outline-primary" onClick={nextPage} disabled={page === totalPages}>
              Next ➡️
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default DailyEmailStats;
